import { forwardRef, useId, memo, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { Message } from '../Message';

import type { CheckboxProps, FontSizeValue } from './Checkbox.types';
import type { Theme } from '../../theme';

/**
 * Map theme font size token to actual value
 */
const getFontSizeValue = (fontSize: FontSizeValue | undefined, theme: Theme): string | undefined => {
  if (!fontSize) {
    return undefined;
  }
  
  // Check if it's a theme token key
  const themeTokens = ['xs', 'sm', 'base', 'md', 'lg', 'xl'] as const;
  if (themeTokens.includes(fontSize as typeof themeTokens[number])) {
    return theme.typography.fontSize[fontSize as keyof typeof theme.typography.fontSize];
  }
  
  // Otherwise, treat as CSS value
  return typeof fontSize === 'number' ? `${fontSize}px` : String(fontSize);
};

/**
 * Container for the checkbox and label
 */
const CheckboxContainer = styled.div<{
  $disabled?: boolean;
  $labelPosition: 'left' | 'right';
}>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 24px;
  width: fit-content;
  max-width: 100%;
  outline: none;
  border-radius: 4px;
  position: relative;
  padding: 1px 0;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  flex-direction: ${({ $labelPosition }) =>
    $labelPosition === 'left' ? 'row-reverse' : 'row'};
`;

/**
 * Hidden input for accessibility
 */
const HiddenCheckbox = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

/**
 * Styled checkbox box
 */
const StyledCheckbox = styled.span<{
  theme?: Theme;
  $checked: boolean;
  $disabled: boolean;
  $error: boolean;
  $indeterminate: boolean;
  $color?: string;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  min-width: 20px;
  height: 20px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  margin: 2px;
  border-radius: 4px;
  border: 1px solid
    ${({ theme, $error, $checked, $indeterminate, $color }) =>
      $error
        ? theme.colors.semantic.red.base
        : $checked || $indeterminate
        ? $color || theme.colors.highlight
        : theme.colors.grey[400]};
  background: ${({ theme, $checked, $indeterminate, $error, $color }) =>
    $checked || $indeterminate
      ? $error
        ? theme.colors.semantic.red.base
        : $color || theme.colors.highlight
      : 'transparent'};
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  flex-shrink: 0;
  position: relative;

  /* Hover effect */
  ${({ $disabled, theme }) =>
    !$disabled &&
    `
    &:hover {
      box-shadow: 0 0 0 2px ${theme.colors.transparent.black20};
    }
  `}

  /* Disabled styling */
  ${({ $disabled, theme }) =>
    $disabled &&
    `
    border-color: ${theme.colors.grey[300]};
    opacity: 0.6;
    background: ${theme.colors.grey[100]};
  `}
`;

/**
 * Checkmark icon (visible when checked)
 */
const CheckIcon = styled.span<{
  theme?: Theme;
  $visible: boolean;
}>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  transition: opacity 0.2s ease-in-out;
  
  &::before {
    content: '';
    display: block;
    width: 5px;
    height: 9px;
    border: solid ${({ theme }) => theme.colors.highlightText};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg) translateY(-1px);
  }
`;

/**
 * Indeterminate icon (horizontal line when indeterminate)
 */
const IndeterminateIcon = styled.span<{
  theme?: Theme;
  $visible: boolean;
}>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  transition: opacity 0.2s ease-in-out;
  
  &::before {
    content: '';
    display: block;
    width: 10px;
    height: 2px;
    background: ${({ theme }) => theme.colors.highlightText};
    border-radius: 1px;
  }
`;

/**
 * Label text for the checkbox
 */
const CheckboxLabel = styled.label<{
  theme?: Theme;
  $disabled?: boolean;
  $labelPosition: 'left' | 'right';
  $fontSize?: string;
}>`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme, $fontSize }) => $fontSize || theme.typography.fontSize.sm};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.grey[400] : theme.colors.grey[700]};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: inline-block;
  min-height: 24px;
  line-height: 24px;
  user-select: none;
  
  /* Allow text wrapping */
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

/**
 * Wrapper for the entire checkbox component
 */
const CheckboxWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * Container for messages
 */
const MessageContainer = styled.div`
  margin-left: 30px;
  margin-top: 2px;
`;

/**
 * Checkbox - A single checkbox component
 *
 * Used for selecting multiple options from a set or toggling a single option.
 * Usually used within a CheckboxGroup, but can also be used standalone.
 *
 * @example
 * ```tsx
 * // Controlled
 * <Checkbox
 *   label="Accept terms and conditions"
 *   checked={accepted}
 *   onChange={(e, checked) => setAccepted(checked)}
 * />
 *
 * // With indeterminate state
 * <Checkbox
 *   label="Select all"
 *   checked={allSelected}
 *   indeterminate={someSelected && !allSelected}
 *   onChange={handleSelectAll}
 * />
 * ```
 */
const CheckboxComponent = forwardRef<HTMLDivElement, CheckboxProps>(
  (
    {
      label,
      labelPosition = 'right',
      checked = false,
      disabled = false,
      error = false,
      color,
      indeterminate = false,
      name,
      id: providedId,
      'aria-label': ariaLabel,
      onChange,
      tabIndex,
      errorMessage,
      assistiveMessage,
      className,
      'data-testid': testId,
      labelFontSize,
    },
    ref
  ) => {
    const generatedId = useId();
    const checkboxId = providedId || generatedId;
    const labelId = `${checkboxId}-label`;
    const hasVisibleLabel = Boolean(label);
    const theme = useTheme() as Theme;
    
    // Resolve font size
    const resolvedLabelFontSize = getFontSizeValue(labelFontSize, theme);

    // Internal state for controlled/uncontrolled behavior
    const [isChecked, setIsChecked] = useState(!!checked);

    // Sync with controlled prop
    useEffect(() => {
      setIsChecked(!!checked);
    }, [checked]);

    const actualTabIndex = tabIndex !== undefined ? tabIndex : disabled ? -1 : 0;

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;

      event.preventDefault();
      event.stopPropagation();
      
      const newChecked = !isChecked;
      setIsChecked(newChecked);
      onChange?.(event, newChecked);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        
        const newChecked = !isChecked;
        setIsChecked(newChecked);
        onChange?.(event, newChecked);
      }
    };

    return (
      <CheckboxWrapper className={className} data-testid={testId}>
        <CheckboxContainer
          ref={ref}
          role="checkbox"
          aria-checked={indeterminate ? 'mixed' : isChecked}
          aria-disabled={disabled}
          aria-labelledby={hasVisibleLabel ? labelId : undefined}
          aria-label={!hasVisibleLabel ? ariaLabel || 'Checkbox' : undefined}
          tabIndex={actualTabIndex}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          $disabled={disabled}
          $labelPosition={labelPosition}
          data-testid={testId && `${testId}-container`}
        >
          <HiddenCheckbox
            type="checkbox"
            id={checkboxId}
            name={name}
            checked={isChecked}
            disabled={disabled}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
          />
          <StyledCheckbox
            $checked={isChecked}
            $disabled={disabled}
            $error={error}
            $indeterminate={indeterminate}
            $color={color}
            data-testid={testId && `${testId}-box`}
          >
            {indeterminate ? (
              <IndeterminateIcon $visible={true} />
            ) : (
              <CheckIcon $visible={isChecked} />
            )}
          </StyledCheckbox>
          {label && (
            <CheckboxLabel
              id={labelId}
              htmlFor={checkboxId}
              $disabled={disabled}
              $labelPosition={labelPosition}
              $fontSize={resolvedLabelFontSize}
            >
              {label}
            </CheckboxLabel>
          )}
        </CheckboxContainer>
        {errorMessage && error && (
          <MessageContainer>
            <Message variant="error">{errorMessage}</Message>
          </MessageContainer>
        )}
        {assistiveMessage && !errorMessage && (
          <MessageContainer>
            <Message variant="assistive">{assistiveMessage}</Message>
          </MessageContainer>
        )}
      </CheckboxWrapper>
    );
  }
);

CheckboxComponent.displayName = 'Checkbox';

export const Checkbox = memo(CheckboxComponent);

export default Checkbox;
